import { defineComponent, computed, ref, watch, ComputedRef } from 'vue';
import dayjs from 'dayjs';
import { useDisabled, useReadonly, usePrefixClass } from '@tdesign/shared-hooks';

import { isArray, isFunction } from 'lodash-es';

import props from './date-range-picker-props';
import {
  DateValue,
  DateRangePickerPartial,
  DatePickerYearChangeTrigger,
  DatePickerMonthChangeTrigger,
  PickerDateRange,
} from './type';

import { RangeInputPopup as TRangeInputPopup } from '../range-input';
import TRangePanel from './components/panel/RangePanel';
import { useRange } from './hooks/useRange';
import {
  parseToDayjs,
  formatTime,
  formatDate,
  isValidDate,
  getDefaultFormat,
  initYearMonthTime,
} from '@tdesign/common-js/date-picker/format';
import { subtractMonth, addMonth, extractTimeObj, getRangeBounds } from '@tdesign/common-js/date-picker/utils';
import { dateCorrection, triggerMap } from './utils';

export default defineComponent({
  name: 'TDateRangePicker',
  props,
  setup(props, { slots }) {
    const COMPONENT_NAME = usePrefixClass('date-range-picker');

    const {
      inputValue,
      popupVisible,
      rangeInputProps,
      popupProps,
      value,
      year,
      month,
      time,
      cacheValue,
      activeIndex,
      isHoverCell,
      isFirstValueSelected,
      onRawChange,
    } = useRange(props);

    const isDisabled = useDisabled() as ComputedRef<boolean | Array<boolean>>;
    const isReadOnly = useReadonly();

    const formatRef = computed(() =>
      getDefaultFormat({
        mode: props.mode,
        enableTimePicker: props.enableTimePicker,
        format: props.format,
        valueType: props.valueType,
      }),
    );

    // 记录面板是否选中过
    const isSelected = ref(false);

    watch(popupVisible, (visible) => {
      // 面板展开重置数据
      if (visible) {
        if (isSingleSideDisabled()) activeIndex.value = isArray(props.disabled) && props.disabled[0] ? 1 : 0;

        isSelected.value = false;
        cacheValue.value = formatDate(value.value || [], {
          format: formatRef.value.valueType,
          targetFormat: formatRef.value.format,
        }) as string[];
        time.value = formatTime(
          value.value || [dayjs().format(formatRef.value.timeFormat), dayjs().format(formatRef.value.timeFormat)],
          formatRef.value.format,
          formatRef.value.timeFormat,
          props.defaultTime,
        ) as string[];

        // 空数据重置为当前年月
        if (!value.value.length) {
          if ((props.range && isArray(props.range)) || props.panelActiveDate) {
            let startRange = props.range as PickerDateRange;
            let endRange = props.range as PickerDateRange;
            if (isArray(props.range)) {
              const [first, second] = props.range;
              if (isArray(first) || isFunction(first) || isArray(second) || isFunction(second)) {
                startRange = first as PickerDateRange;
                endRange = second as PickerDateRange;
              }
            }

            const startRangeBounds = getRangeBounds(startRange);
            const endRangeBounds = getRangeBounds(endRange);

            const startYearFromRange = startRangeBounds.min?.getFullYear() ?? startRangeBounds.max?.getFullYear();
            const startMonthFromRange = startRangeBounds.min?.getMonth() ?? startRangeBounds.max?.getMonth();

            const endYearFromRange = endRangeBounds.min?.getFullYear() ?? endRangeBounds.max?.getFullYear();
            const endMonthFromRange = endRangeBounds.min?.getMonth() ?? endRangeBounds.max?.getMonth();

            let startPanelActiveDate = props.panelActiveDate as any;
            let endPanelActiveDate = props.panelActiveDate as any;
            if (isArray(props.panelActiveDate)) {
              [startPanelActiveDate, endPanelActiveDate] = props.panelActiveDate;
            }

            const leftYear = (startPanelActiveDate?.year ?? startYearFromRange) as number;
            const leftMonth = startPanelActiveDate?.month
              ? Number(startPanelActiveDate?.month) - 1
              : startMonthFromRange;
            const rightYear = (endPanelActiveDate?.year ?? endYearFromRange) as number;
            const rightMonth = endPanelActiveDate?.month ? Number(endPanelActiveDate?.month) - 1 : endMonthFromRange;

            // 获取默认值作为兜底
            const { year: defaultYear, month: defaultMonth } = initYearMonthTime({
              value: value.value,
              mode: props.mode,
              format: formatRef.value.format,
              enableTimePicker: props.enableTimePicker,
            });

            year.value = [leftYear ?? defaultYear[0], rightYear ?? defaultYear[1]];
            month.value = [leftMonth ?? defaultMonth[0], rightMonth ?? defaultMonth[1]];

            // 修正：如果左右面板年月完全一致且未显式指定不同范围，则偏移面板
            if (year.value[0] === year.value[1] && month.value[0] === month.value[1] && !props.enableTimePicker) {
              if (startRange === endRange) {
                if (props.mode === 'year') year.value[1] += 10;
                else if (props.mode === 'month' || props.mode === 'quarter') year.value[1] += 1;
                else month.value[1] += 1;
              }
            }
          } else {
            const { year: defaultYear, month: defaultMonth } = initYearMonthTime({
              value: value.value,
              mode: props.mode,
              format: formatRef.value.format,
              enableTimePicker: props.enableTimePicker,
            });
            year.value = defaultYear;
            month.value = defaultMonth;
          }
        } else if (value.value.length === 2 && !props.enableTimePicker) {
          // 确保右侧面板月份比左侧大 避免两侧面板月份一致
          const nextMonth = value.value.map((v: string) => parseToDayjs(v, formatRef.value.format).month());
          year.value = value.value.map((v: string) => parseToDayjs(v, formatRef.value.valueType).year());
          if (year.value[0] === year.value[1] && nextMonth[0] === nextMonth[1]) {
            nextMonth[0] === 11 ? (nextMonth[0] -= 1) : (nextMonth[1] += 1);
          }
          month.value = nextMonth;
          // 月份季度选择时需要确保右侧面板年份比左侧大
          if ((props.mode === 'month' || props.mode === 'quarter') && year.value[0] === year.value[1]) {
            year.value = [year.value[0], year.value[0] + 1];
          }
        } else {
          year.value = value.value.map((v: string) => parseToDayjs(v, formatRef.value.format).year());
          if (year.value.length === 1) year.value = [year.value[0], year.value[0]];

          month.value = value.value.map((v: string) => parseToDayjs(v, formatRef.value.format).month());
          if (month.value.length === 1) month.value = [month.value[0], Math.min(month.value[0] + 1, 11)];
        }
      } else {
        activeIndex.value = 0;
        isHoverCell.value = false;
        isFirstValueSelected.value = false;
        if (props.needConfirm) {
          inputValue.value = formatDate(value.value, {
            format: formatRef.value.valueType,
            targetFormat: formatRef.value.format,
          });
        } else {
          confirmValueChange();
        }
      }
    });

    function normalizeRangeValue(nextValue: string[]) {
      return [nextValue[0] || '', nextValue[1] || ''];
    }

    function getInvalidIndex(nextValue: string[]) {
      return nextValue.findIndex((v, index) => {
        if (!v) return !(isArray(props.disabled) && props.disabled[index]);
        return !isValidDate(v, formatRef.value.format);
      });
    }

    function getDayjsValue(nextValue: string[]) {
      return nextValue.map((v, i) =>
        v ? parseToDayjs(v, formatRef.value.format, undefined, undefined, props.defaultTime?.[i]) : null,
      );
    }

    function isSingleSideDisabled() {
      return isArray(props.disabled) && props.disabled.filter(Boolean).length === 1;
    }

    function getAvailableActiveIndex() {
      if (!isArray(props.disabled)) return activeIndex.value;
      if (props.disabled[0] && !props.disabled[1]) return 1;
      if (!props.disabled[0] && props.disabled[1]) return 0;
      return activeIndex.value;
    }

    // 日期 hover
    function onCellMouseEnter(date: Date) {
      isHoverCell.value = true;
      const nextValue = [...(inputValue.value as string[])];
      nextValue[activeIndex.value] = formatDate(date, {
        format: formatRef.value.format,
      }) as string;
      inputValue.value = nextValue;
    }

    // 日期 leave
    function onCellMouseLeave() {
      isHoverCell.value = false;
      inputValue.value = cacheValue.value;
    }

    // 日期点击
    function onCellClick(date: Date, { e }: { e: MouseEvent; partial: DateRangePickerPartial }) {
      const nextActiveIndex = getAvailableActiveIndex();
      activeIndex.value = nextActiveIndex as 0 | 1;

      props.onPick?.(date, { e, partial: nextActiveIndex ? 'end' : 'start' });

      isHoverCell.value = false;
      isSelected.value = true;

      const nextValue = [...(inputValue.value as string[])];
      nextValue[nextActiveIndex] = formatDate(date, {
        format: formatRef.value.format,
      }) as string;
      const normalizedValue = normalizeRangeValue(nextValue);
      cacheValue.value = normalizedValue;
      inputValue.value = normalizedValue;

      // 有时间选择器走 confirm 逻辑
      if (props.enableTimePicker) {
        const selectedValue = [...normalizedValue];
        const selectedActiveIndex = nextActiveIndex;
        setTimeout(() => {
          popupVisible.value = true;
          activeIndex.value = selectedActiveIndex as 0 | 1;
          isSelected.value = true;
          cacheValue.value = selectedValue;
          inputValue.value = selectedValue;
        });
        return;
      }

      // 确保两端都是有效值
      const notValidIndex = getInvalidIndex(normalizedValue);

      // 当两端都有有效值时更改 value
      if (notValidIndex === -1 && normalizedValue.length === 2) {
        // 二次修改时当其中一侧不符合上次区间规范时，清空另一侧数据
        if (
          !isFirstValueSelected.value &&
          normalizedValue[0] &&
          normalizedValue[1] &&
          parseToDayjs(normalizedValue[0], formatRef.value.format).isAfter(
            parseToDayjs(normalizedValue[1], formatRef.value.format),
          )
        ) {
          normalizedValue[activeIndex.value ? 0 : 1] = '';
          cacheValue.value = normalizedValue;
          inputValue.value = normalizedValue;
        } else {
          onRawChange?.(
            formatDate(normalizedValue, {
              format: formatRef.value.format,
              targetFormat: formatRef.value.valueType,
              autoSwap: true,
              defaultTime: props.defaultTime,
            }) as DateValue[],
            {
              dayjsValue: getDayjsValue(normalizedValue),
              trigger: 'pick',
            },
          );
        }
      }

      if (Array.isArray(props.disabled)) return;

      // 首次点击不关闭、确保两端都有有效值并且无时间选择器时点击后自动关闭
      if (!isFirstValueSelected.value) {
        let nextIndex = notValidIndex;
        if (nextIndex === -1) nextIndex = activeIndex.value ? 0 : 1;
        activeIndex.value = nextIndex as 0 | 1;
        isFirstValueSelected.value = normalizedValue.some((v) => !!v);
      } else {
        popupVisible.value = false;
      }
    }

    // 头部快速切换
    function onJumperClick({
      trigger,
      partial,
    }: {
      trigger: 'prev' | 'next' | 'current';
      partial: DateRangePickerPartial;
    }) {
      const partialIndex = partial === 'start' ? 0 : 1;

      const monthCountMap = { date: 1, week: 1, month: 12, quarter: 12, year: 120 };
      const monthCount = monthCountMap[props.mode] || 0;
      const current = new Date(year.value[partialIndex], month.value[partialIndex]);

      let next = null;
      if (trigger === 'prev') {
        next = subtractMonth(current, monthCount);
      } else if (trigger === 'current') {
        next = new Date();
      } else if (trigger === 'next') {
        next = addMonth(current, monthCount);
      }

      let nextYear = [...year.value];
      nextYear[partialIndex] = next.getFullYear();
      let nextMonth = [...month.value];
      nextMonth[partialIndex] = next.getMonth();
      const onlyYearSelect = ['year', 'quarter', 'month'].includes(props.mode);

      // 头部日期切换修正
      const correctedDate = dateCorrection(partialIndex, nextYear, nextMonth, onlyYearSelect);
      nextYear = correctedDate.nextYear;
      nextMonth = correctedDate.nextMonth;

      const yearChanged = year.value[partialIndex] !== nextYear[partialIndex];
      const monthChanged = month.value[partialIndex] !== nextMonth[partialIndex];

      year.value = nextYear;
      month.value = nextMonth;

      // 触发年份变化事件
      if (yearChanged) {
        props.onYearChange?.({
          partial,
          year: nextYear[partialIndex],
          date: value.value.map((v) => dayjs(v).toDate()),
          trigger: trigger === 'current' ? 'today' : (`year-${triggerMap[trigger]}` as DatePickerYearChangeTrigger),
        });
      }

      // 触发月份变化事件
      if (monthChanged) {
        props.onMonthChange?.({
          partial,
          month: nextMonth[partialIndex],
          date: value.value.map((v) => dayjs(v).toDate()),
          trigger: trigger === 'current' ? 'today' : (`month-${triggerMap[trigger]}` as DatePickerMonthChangeTrigger),
        });
      }
    }

    // time-picker 点击
    function onTimePickerChange(val: string) {
      const { hours, minutes, seconds, milliseconds, meridiem } = extractTimeObj(val);

      const nextInputValue = [...(inputValue.value as DateValue[])];
      const changedInputValue = nextInputValue[activeIndex.value];
      const currentDate = !dayjs(changedInputValue, formatRef.value.format).isValid()
        ? dayjs().year(year.value[activeIndex.value]).month(month.value[activeIndex.value])
        : dayjs(changedInputValue, formatRef.value.format);
      // am pm 12小时制转化 24小时制
      let nextHours = hours;
      if (/am/i.test(meridiem) && nextHours === 12) nextHours -= 12;
      if (/pm/i.test(meridiem) && nextHours < 12) nextHours += 12;

      const nextDate = currentDate.hour(nextHours).minute(minutes).second(seconds).millisecond(milliseconds).toDate();
      nextInputValue[activeIndex.value] = nextDate;

      const nextTime = [...time.value];
      nextTime[activeIndex.value] = val;
      time.value = nextTime;

      isSelected.value = true;
      inputValue.value = formatDate(nextInputValue, {
        format: formatRef.value.format,
      });
      cacheValue.value = formatDate(nextInputValue, {
        format: formatRef.value.format,
      });
    }
    const confirmValueChange = (e?: MouseEvent) => {
      const nextValue = normalizeRangeValue([...(inputValue.value as string[])]);

      const notValidIndex = getInvalidIndex(nextValue);

      // 当两端都有有效值时更改 value
      if (notValidIndex === -1 && nextValue.length === 2) {
        // 二次修改时当其中一侧不符合上次区间规范时，清空另一侧数据
        if (
          !isFirstValueSelected.value &&
          nextValue[0] &&
          nextValue[1] &&
          parseToDayjs(nextValue[0], formatRef.value.format).isAfter(parseToDayjs(nextValue[1], formatRef.value.format))
        ) {
          nextValue[activeIndex.value ? 0 : 1] = '';
          cacheValue.value = nextValue;
          inputValue.value = nextValue;
        } else {
          const formattedValue = formatDate(nextValue, {
            format: formatRef.value.format,
            targetFormat: formatRef.value.valueType,
            autoSwap: true,
            defaultTime: props.defaultTime,
          }) as DateValue[];
          const isSame =
            Array.isArray(formattedValue) &&
            formattedValue.length === 2 &&
            formattedValue[0] != null &&
            formattedValue[1] != null &&
            value.value[0] != null &&
            value.value[1] != null &&
            dayjs(formattedValue[0] as any).valueOf() === dayjs(value.value[0] as any).valueOf() &&
            dayjs(formattedValue[1] as any).valueOf() === dayjs(value.value[1] as any).valueOf();
          //判断传入的值和当前值是否相同，不同再触发 onChange，避免不必要的事件触发
          props?.onConfirm?.({
            date: nextValue.map((v) => (v ? dayjs(v).toDate() : null)) as Date[],
            e: e || null,
            partial: activeIndex.value ? 'end' : 'start',
          });
          if (!isSame) {
            onRawChange?.(formattedValue, {
              dayjsValue: getDayjsValue(nextValue),
              trigger: 'confirm',
            });
          }
        }
      }
    };
    // 确定
    function onConfirmClick({ e }: { e: MouseEvent }) {
      confirmValueChange(e);

      const nextValue = normalizeRangeValue([...(inputValue.value as string[])]);

      const notValidIndex = getInvalidIndex(nextValue);

      if (isSingleSideDisabled()) {
        popupVisible.value = false;
      } else if (!isFirstValueSelected.value) {
        // 首次点击不关闭、确保两端都有有效值并且无时间选择器时点击后自动关闭
        let nextIndex = notValidIndex;
        if (nextIndex === -1) nextIndex = activeIndex.value ? 0 : 1;
        activeIndex.value = nextIndex as 0 | 1;
        isFirstValueSelected.value = nextValue.some((v) => !!v);
      } else if (nextValue.length === 2) {
        popupVisible.value = false;
      }
    }

    // 预设
    function onPresetClick(preset: any, context: any) {
      let presetValue = preset;
      if (isFunction(preset)) {
        presetValue = preset();
      }
      if (!isArray(presetValue)) {
        console.error(`preset: ${preset} 预设值必须是数组!`);
      } else {
        onRawChange?.(
          formatDate(presetValue, {
            format: formatRef.value.format,
            targetFormat: formatRef.value.valueType,
            autoSwap: true,
            defaultTime: props.defaultTime,
          }) as DateValue[],
          {
            dayjsValue: presetValue.map((p, i) =>
              parseToDayjs(p, formatRef.value.format, undefined, undefined, props.defaultTime?.[i]),
            ),
            trigger: 'preset',
          },
        );
        // 更新到 input，避免 needConfirm 导致值被覆盖
        inputValue.value = formatDate(presetValue, {
          format: formatRef.value.format,
        });
        popupVisible.value = false;
        props.onPresetClick?.(context);
      }
    }

    function onYearChange(nextVal: number, { partial }: { partial: DateRangePickerPartial }) {
      let partialIndex = partial === 'start' ? 0 : 1;
      if (props.enableTimePicker) partialIndex = activeIndex.value;

      let nextYear = [...year.value];
      let nextMonth = [...month.value];

      nextYear[partialIndex] = nextVal;
      const onlyYearSelect = ['year', 'quarter', 'month'].includes(props.mode);

      // 头部日期切换修正
      const correctedDate = dateCorrection(partialIndex, nextYear, nextMonth, onlyYearSelect);
      nextYear = correctedDate.nextYear;
      nextMonth = correctedDate.nextMonth;

      year.value = nextYear;
      if (!onlyYearSelect) month.value = nextMonth;

      props.onYearChange?.({
        partial,
        year: nextYear[partialIndex],
        date: value.value.map((v) => dayjs(v).toDate()),
        trigger: 'year-select',
      });
    }

    function onMonthChange(nextVal: number, { partial }: { partial: DateRangePickerPartial }) {
      let partialIndex = partial === 'start' ? 0 : 1;
      if (props.enableTimePicker) partialIndex = activeIndex.value;

      const nextMonth = [...month.value];
      nextMonth[partialIndex] = nextVal;
      // 保证左侧时间不大于右侧
      if (year.value[0] === year.value[1]) {
        if (partialIndex === 0) {
          // 操作了左侧区间, 处理右侧区间小于或等于左侧区间的场景，交互上始终报错右侧比左侧大 1
          if (nextMonth[1] <= nextMonth[0]) {
            nextMonth[1] = nextMonth[0] + 1;
            if (nextMonth[1] === 12) {
              // 处理跨年的边界场景
              nextMonth[1] = 0;
              year.value = [year.value?.[0], year.value?.[1] + 1];
            }
          }
        }
        if (partialIndex === 1) {
          // 操作了右侧区间, 处理右侧区间小于或等于左侧区间的场景，交互上始终报错左侧比右侧小 1
          nextMonth[0] = Math.min(nextMonth[0], nextMonth[1]);
          if (nextMonth[0] >= nextMonth[1]) {
            nextMonth[0] -= 1;
            if (nextMonth[0] === -1) {
              // 处理跨年的边界场景
              nextMonth[0] = 11;
              year.value = [year.value?.[0] - 1, year.value?.[1]];
            }
          }
        }
      }

      month.value = nextMonth;

      props.onMonthChange?.({
        partial,
        month: nextMonth[partialIndex],
        date: value.value.map((v) => dayjs(v).toDate()),
        trigger: 'month-select',
      });
    }

    const panelProps = computed(() => ({
      hoverValue: (isHoverCell.value ? inputValue.value : []) as string[],
      value: (isSelected.value ? cacheValue.value : value.value) as string[],
      isFirstValueSelected: isFirstValueSelected.value,
      activeIndex: activeIndex.value,
      year: year.value,
      month: month.value,
      format: formatRef.value.format,
      mode: props.mode,
      presets: props.presets,
      time: time.value,
      disableDate: props.disableDate,
      disabled: isDisabled.value,
      disableTime: props.disableTime,
      firstDayOfWeek: props.firstDayOfWeek,
      timePickerProps: props.timePickerProps,
      enableTimePicker: props.enableTimePicker,
      presetsPlacement: props.presetsPlacement,
      popupVisible: popupVisible.value,
      range: props.range,
      panelPreselection: props.panelPreselection,
      cancelRangeSelectLimit: props.cancelRangeSelectLimit,
      needConfirm: props.needConfirm,
      onCellClick,
      onCellMouseEnter,
      onCellMouseLeave,
      onJumperClick,
      onConfirmClick,
      onPresetClick,
      onYearChange,
      onMonthChange,
      onTimePickerChange,
      cell: props.cell,
    }));

    return () => (
      <div class={COMPONENT_NAME.value}>
        <TRangeInputPopup
          readonly={isReadOnly.value}
          disabled={isDisabled.value}
          label={props.label}
          status={props.status}
          tips={props.tips || slots.tips}
          inputValue={inputValue.value as string[]}
          popupProps={popupProps.value}
          rangeInputProps={rangeInputProps.value}
          popupVisible={popupVisible.value}
          panel={() => <TRangePanel {...panelProps.value} v-slots={slots} />}
        />
      </div>
    );
  },
});
